import axios from 'axios';

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

// ── Decode role directly from JWT — no extra API call needed ──
const getUserRoleFromToken = () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return 'Manufacturer';
    const payload = token.split('.')[1];
    const decoded = JSON.parse(atob(payload));
    return decoded.role || 'Manufacturer';
  } catch {
    return 'Manufacturer';
  }
};

// ── Login (email or username — backend uses email field for both) ──
// ── Login (email or username — both use email field now) ──
export const loginWithEmail = async (email, password) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/login`, {
      email,
      password
    });

    if (response.data?.success) {
      if (response.data.data?.token) {
        localStorage.setItem('authToken', response.data.data.token);
      }
      return {
        success: true,
        message: response.data.message || 'Login successful',
        data: response.data.data  // { id, username, email, phone, type, token }
      };
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Login failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Login failed');
    }
  }
};

export const loginWithUsername = async (username, password) => {
  try {
    // ── New /api/login only supports email, so username login
    //    needs to look up email first or just try as email ──
    const response = await axios.post(`${API_BASE_URL}/login`, {
      email: username,  // user might type email in username field
      password
    });

    if (response.data?.success) {
      if (response.data.data?.token) {
        localStorage.setItem('authToken', response.data.data.token);
      }
      return {
        success: true,
        message: response.data.message || 'Login successful',
        data: response.data.data
      };
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Login failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Login failed');
    }
  }
};

// ── Signup ──
export const signup = async (username, email, phone, password, type) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/user-admin`, {  // ← correct endpoint
      username,
      email,
      phone,
      password,
      type    // ← role e.g. "Recycler"
    });
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Signup failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Signup failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Signup failed');
    }
  }
};

// ── Update Password ──
export const updatePassword = async (email, currentPassword, newPassword) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/users/update-password`, {
      email, currentPassword, newPassword
    });
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Password update failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Password update failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Password update failed');
    }
  }
};

// ── Forgot Password ──
export const forgotPassword = async (email, newPassword) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/users/forgot-password`, {
      email, newPassword
    });
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Password reset failed');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Password reset failed');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Password reset failed');
    }
  }
};

// ── Fetch Product Passport ──
// Role is decoded from JWT — no separate API call needed
export const fetchProductPassport = async (dppId) => {
  try {
    const role = getUserRoleFromToken(); // ← decoded from JWT
    const token = localStorage.getItem('authToken');

    const response = await axios.get(`${API_BASE_URL}/role/${dppId}`, {
      headers: {
        'X-User-Role':   role,                    // ← role for category filtering
        'Authorization': `Bearer ${token}`        // ← JWT for auth middleware
      }
    });

    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch product passport');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch product passport');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch product passport');
    }
  }
};

// ── Get User Profile ──
export const getUserProfile = async (userId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/users/${userId}`);
    if (response.data && response.data.success) {
      return response.data;
    } else {
      throw new Error(response.data.message || 'Failed to fetch user profile');
    }
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data.message || 'Failed to fetch user profile');
    } else if (error.request) {
      throw new Error('No response from server. Please check your connection.');
    } else {
      throw new Error(error.message || 'Failed to fetch user profile');
    }
  }
};